import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ShellHeader from '../components/ShellHeader';
import { formatEngineSizeForDisplay } from '../utils/engineSize';

function VehicleDetailsPage({ authUser }) {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [id]);

  useEffect(() => {
    const loadVehicle = async () => {
      try {
        setLoading(true);
        setMessage('');

        const response = await fetch(`/api/vehicles/${id}`);

        if (response.status === 404) {
          setVehicle(null);
          setMessage('Vehicle not found.');
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to load vehicle details.');
        }

        const data = await response.json();
        setVehicle(data);
        setSelectedImageIndex(0);
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadVehicle();
  }, [id]);

  const galleryImages = vehicle?.imageUrls?.length ? vehicle.imageUrls : [vehicle?.imageUrl].filter(Boolean);

  useEffect(() => {
    if (!isLightboxOpen || galleryImages.length === 0) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsLightboxOpen(false);
        return;
      }

      if (event.key === 'ArrowLeft') {
        setSelectedImageIndex((current) => (
          (current - 1 + galleryImages.length) % galleryImages.length
        ));
      }

      if (event.key === 'ArrowRight') {
        setSelectedImageIndex((current) => (current + 1) % galleryImages.length);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [galleryImages.length, isLightboxOpen]);

  const openLightboxAt = (index) => {
    setSelectedImageIndex(index);
    setIsLightboxOpen(true);
  };

  const showPreviousImage = () => {
    setSelectedImageIndex((current) => (
      (current - 1 + galleryImages.length) % galleryImages.length
    ));
  };

  const showNextImage = () => {
    setSelectedImageIndex((current) => (current + 1) % galleryImages.length);
  };

  return (
    <div className="site-shell">
      <ShellHeader authUser={authUser} />

      <main className="content-wrap content-wrap-single">
        <section className="vehicle-details-block">
          <div className="section-header">
            <h2>Vehicle Details</h2>
            <Link to="/" className="ghost-btn vehicle-back-link">Back to Stock</Link>
          </div>

          {loading && <p className="status-text">Loading vehicle...</p>}
          {!loading && message && <p className="status-text">{message}</p>}

          {!loading && vehicle && (
            <div className="vehicle-details-layout">
              <div className="vehicle-gallery-panel">
                <div className="vehicle-gallery-main">
                  <button
                    type="button"
                    className="vehicle-gallery-main-btn"
                    onClick={() => openLightboxAt(selectedImageIndex)}
                    aria-label="Open image carousel"
                  >
                    <img
                      src={galleryImages[selectedImageIndex]}
                      alt={`${vehicle.make} ${vehicle.model}`}
                    />
                  </button>
                </div>

                {galleryImages.length > 1 && (
                  <div className="vehicle-gallery-thumbs">
                    {galleryImages.map((image, index) => (
                      <button
                        type="button"
                        key={`${vehicle.id}-gallery-${index}`}
                        className={`vehicle-gallery-thumb ${selectedImageIndex === index ? 'active' : ''}`}
                        onClick={() => openLightboxAt(index)}
                        aria-label={`View image ${index + 1} in carousel`}
                      >
                        <img src={image} alt={`${vehicle.make} ${vehicle.model} view ${index + 1}`} />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {isLightboxOpen && galleryImages.length > 0 && (
                <div
                  className="vehicle-lightbox"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Vehicle image carousel"
                >
                  <button
                    type="button"
                    className="vehicle-lightbox-overlay"
                    onClick={() => setIsLightboxOpen(false)}
                    aria-label="Close image carousel"
                  />

                  <div className="vehicle-lightbox-content">
                    <button
                      type="button"
                      className="vehicle-lightbox-close"
                      onClick={() => setIsLightboxOpen(false)}
                      aria-label="Close image carousel"
                    >
                      ×
                    </button>

                    {galleryImages.length > 1 && (
                      <button
                        type="button"
                        className="vehicle-lightbox-nav vehicle-lightbox-nav-prev"
                        onClick={showPreviousImage}
                        aria-label="Previous image"
                      >
                        ‹
                      </button>
                    )}

                    <img
                      src={galleryImages[selectedImageIndex]}
                      alt={`${vehicle.make} ${vehicle.model} large view ${selectedImageIndex + 1}`}
                    />

                    {galleryImages.length > 1 && (
                      <button
                        type="button"
                        className="vehicle-lightbox-nav vehicle-lightbox-nav-next"
                        onClick={showNextImage}
                        aria-label="Next image"
                      >
                        ›
                      </button>
                    )}

                    <p className="vehicle-lightbox-counter">
                      {selectedImageIndex + 1} / {galleryImages.length}
                    </p>
                  </div>
                </div>
              )}

              <div className="vehicle-details-panel">
                <h1>
                  {vehicle.make} {vehicle.model}
                  {vehicle.isSold && <span className="vehicle-sold-badge">Sold</span>}
                </h1>
                <p>{vehicle.description}</p>

                <div className="vehicle-details-grid">
                  <div>
                    <span>Year</span>
                    <strong>{vehicle.year}</strong>
                  </div>
                  <div>
                    <span>Mileage</span>
                    <strong>{Number(vehicle.mileage).toLocaleString()} miles</strong>
                  </div>
                  <div>
                    <span>Fuel</span>
                    <strong>{(vehicle.fuelType || 'n/a').toUpperCase()}</strong>
                  </div>
                  <div>
                    <span>Gearbox</span>
                    <strong>{(vehicle.gearboxType || 'n/a').toUpperCase()}</strong>
                  </div>
                  <div>
                    <span>Engine</span>
                    <strong>{formatEngineSizeForDisplay(vehicle.engineSize)}</strong>
                  </div>
                  <div>
                    <span>Registration</span>
                    <strong>{vehicle.registrationNumber || 'n/a'}</strong>
                  </div>
                </div>

                <div className="vehicle-price-panel">
                  <strong>£{Number(vehicle.salePrice).toLocaleString()}</strong>
                  <span>Finance from £{Number(vehicle.financePrice).toLocaleString()}/month</span>
                </div>

                <div className="vehicle-cta-panel">
                  <h3>Interested? Get in touch!</h3>
                  <div className="vehicle-cta-actions">
                    <a className="vehicle-cta-link" href="tel:01782835561">
                      <span className="vehicle-cta-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                          <path d="M6.7 3.9h2.8l1.3 4-1.8 1.6a15.5 15.5 0 0 0 5.5 5.5l1.6-1.8 4 1.3v2.8c0 .7-.6 1.3-1.3 1.3A15 15 0 0 1 4.1 5.2c0-.7.6-1.3 1.3-1.3Z" />
                        </svg>
                      </span>
                      Call: 01782 835561
                    </a>
                    <a className="vehicle-cta-link" href="mailto:markjeffery1000@yahoo.co.uk">
                      <span className="vehicle-cta-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                          <rect x="3.2" y="5.3" width="17.6" height="13.4" rx="1.8" />
                          <path d="m4.3 7 7.7 6 7.7-6" />
                        </svg>
                      </span>
                      Email: markjeffery1000@yahoo.co.uk
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default VehicleDetailsPage;